import { DEFAULT_RANGE } from './controlCenterUtils';

export const CONTROL_CENTER_SAVED_SEARCHES_STORAGE_KEY = 'controlCenterSavedSearches';
export const CONTROL_CENTER_SAVED_SEARCHES_VERSION = 1;

export const EMPTY_FILTER_SNAPSHOT = {
  range: DEFAULT_RANGE,
  customDateStart: null,
  customDateEnd: null,
  selectedClients: [],
  selectedStatus: null,
};

const createEmptyStorage = () => ({
  version: CONTROL_CENTER_SAVED_SEARCHES_VERSION,
  defaultId: null,
  searches: [],
});

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

export const getControlCenterSavedSearchesStorageKey = (companyId) => {
  if (companyId == null || companyId === '') return null;
  return `${CONTROL_CENTER_SAVED_SEARCHES_STORAGE_KEY}:${companyId}`;
};

/** One-time: move legacy unscoped key into the first company bucket that loads it. */
const migrateLegacySavedSearchesIfNeeded = (companyId) => {
  const key = getControlCenterSavedSearchesStorageKey(companyId);
  if (!key || typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(key)) return;
    const legacy = window.localStorage.getItem(CONTROL_CENTER_SAVED_SEARCHES_STORAGE_KEY);
    if (!legacy) return;
    window.localStorage.setItem(key, legacy);
    window.localStorage.removeItem(CONTROL_CENTER_SAVED_SEARCHES_STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
};

const normalizeClientEntries = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && item.id != null)
    .map((item) => ({
      id: item.id,
      client_name: typeof item.client_name === 'string' ? item.client_name : 'Unknown',
    }));
};

export const captureFilterSnapshot = (filterState) => ({
  range: typeof filterState.range === 'string' ? filterState.range : DEFAULT_RANGE,
  customDateStart: filterState.customDateStart instanceof Date
    ? filterState.customDateStart.toISOString()
    : (typeof filterState.customDateStart === 'string' ? filterState.customDateStart : null),
  customDateEnd: filterState.customDateEnd instanceof Date
    ? filterState.customDateEnd.toISOString()
    : (typeof filterState.customDateEnd === 'string' ? filterState.customDateEnd : null),
  selectedClients: normalizeClientEntries(filterState.selectedClients),
  selectedStatus: filterState.selectedStatus != null ? String(filterState.selectedStatus) : null,
});

export const normalizeFilterSnapshot = (snapshot) => {
  const source = snapshot && typeof snapshot === 'object' ? snapshot : {};
  return {
    range: typeof source.range === 'string' ? source.range : DEFAULT_RANGE,
    customDateStart: typeof source.customDateStart === 'string' ? source.customDateStart : null,
    customDateEnd: typeof source.customDateEnd === 'string' ? source.customDateEnd : null,
    selectedClients: normalizeClientEntries(source.selectedClients),
    selectedStatus: source.selectedStatus != null ? String(source.selectedStatus) : null,
  };
};

/**
 * Keep only clients that exist in `clientOptions` (current user’s accessible list).
 * Orphans (revoked access / other company) are dropped — never kept by name alone.
 */
export const sanitizeFilterSnapshot = (snapshot, { clientOptions = [] } = {}) => {
  const normalized = normalizeFilterSnapshot(snapshot);

  const clientOptionMap = new Map((clientOptions || []).map((client) => [String(client.id), client]));
  normalized.selectedClients = normalized.selectedClients
    .map((client) => {
      const resolved = clientOptionMap.get(String(client.id));
      if (!resolved) return null;
      return {
        id: resolved.id,
        client_name: resolved.client_name || client.client_name || 'Unknown',
      };
    })
    .filter(Boolean);

  return normalized;
};

export const arraysEqualById = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const aIds = a.map((item) => String(item.id)).sort();
  const bIds = b.map((item) => String(item.id)).sort();
  return aIds.every((id, index) => id === bIds[index]);
};

export const snapshotsMatch = (left, right) => {
  const a = normalizeFilterSnapshot(left);
  const b = normalizeFilterSnapshot(right);

  return (
    a.range === b.range
    && a.customDateStart === b.customDateStart
    && a.customDateEnd === b.customDateEnd
    && arraysEqualById(a.selectedClients, b.selectedClients)
    && a.selectedStatus === b.selectedStatus
  );
};

const normalizeSearchEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  if (!isNonEmptyString(entry.id) || !isNonEmptyString(entry.name)) return null;

  return {
    id: entry.id,
    name: entry.name.trim(),
    createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : new Date().toISOString(),
    updatedAt: typeof entry.updatedAt === 'string' ? entry.updatedAt : new Date().toISOString(),
    filters: normalizeFilterSnapshot(entry.filters),
  };
};

export const normalizeSavedSearchesStorage = (raw) => {
  const empty = createEmptyStorage();
  if (!raw || typeof raw !== 'object') return empty;

  const searches = Array.isArray(raw.searches)
    ? raw.searches.map(normalizeSearchEntry).filter(Boolean)
    : [];

  const defaultId = typeof raw.defaultId === 'string' ? raw.defaultId : null;
  const hasDefault = defaultId && searches.some((search) => search.id === defaultId);

  return {
    version: CONTROL_CENTER_SAVED_SEARCHES_VERSION,
    defaultId: hasDefault ? defaultId : null,
    searches,
  };
};

export const loadControlCenterSavedSearches = (companyId) => {
  try {
    if (!companyId) return createEmptyStorage();
    migrateLegacySavedSearchesIfNeeded(companyId);
    const key = getControlCenterSavedSearchesStorageKey(companyId);
    const stored = window.localStorage.getItem(key);
    if (!stored) return createEmptyStorage();
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return createEmptyStorage();
    if (parsed.version !== CONTROL_CENTER_SAVED_SEARCHES_VERSION) return createEmptyStorage();
    return normalizeSavedSearchesStorage(parsed);
  } catch {
    return createEmptyStorage();
  }
};

export const persistControlCenterSavedSearches = (companyId, data) => {
  const normalized = normalizeSavedSearchesStorage(data);
  const key = getControlCenterSavedSearchesStorageKey(companyId);
  if (!key) return normalized;
  try {
    window.localStorage.setItem(key, JSON.stringify(normalized));
  } catch {
    // ignore quota / private mode
  }
  return normalized;
};

/**
 * Drop inaccessible clients from every saved search for this company and persist when changed.
 * Call after the live client list has loaded for the current company.
 */
export const pruneSavedSearchesAgainstClients = (companyId, storage, clientOptions = []) => {
  const normalized = normalizeSavedSearchesStorage(storage);
  if (!companyId) {
    return { storage: normalized, changed: false };
  }

  let changed = false;

  const searches = normalized.searches.map((search) => {
    const sanitizedFilters = sanitizeFilterSnapshot(search.filters, { clientOptions });
    if (arraysEqualById(search.filters.selectedClients, sanitizedFilters.selectedClients)) {
      const namesChanged = search.filters.selectedClients.some((client, index) => (
        String(client.client_name || '')
        !== String(sanitizedFilters.selectedClients[index]?.client_name || '')
      ));
      if (!namesChanged) return search;
      changed = true;
      return {
        ...search,
        filters: sanitizedFilters,
        updatedAt: new Date().toISOString(),
      };
    }
    changed = true;
    return {
      ...search,
      filters: sanitizedFilters,
      updatedAt: new Date().toISOString(),
    };
  });

  if (!changed) {
    return { storage: normalized, changed: false };
  }

  const nextStorage = persistControlCenterSavedSearches(companyId, {
    ...normalized,
    searches,
  });
  return { storage: nextStorage, changed: true };
};

export const createSearchId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `search-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const upsertSearch = (companyId, storage, { id, name, filters, setAsDefault = false }) => {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (!trimmedName) {
    throw new Error('Saved search name is required.');
  }

  const normalizedFilters = normalizeFilterSnapshot(filters);
  const now = new Date().toISOString();
  const normalized = normalizeSavedSearchesStorage(storage);
  const existingIndex = id ? normalized.searches.findIndex((search) => search.id === id) : -1;
  const nextId = existingIndex >= 0 ? normalized.searches[existingIndex].id : createSearchId();

  const nextSearch = {
    id: nextId,
    name: trimmedName,
    createdAt: existingIndex >= 0 ? normalized.searches[existingIndex].createdAt : now,
    updatedAt: now,
    filters: normalizedFilters,
  };

  const searches = existingIndex >= 0
    ? normalized.searches.map((search, index) => (index === existingIndex ? nextSearch : search))
    : [...normalized.searches, nextSearch];

  const nextStorage = persistControlCenterSavedSearches(companyId, {
    defaultId: setAsDefault ? nextId : normalized.defaultId,
    searches,
  });

  return { storage: nextStorage, searchId: nextId };
};

export const deleteSearch = (companyId, storage, searchId) => {
  const normalized = normalizeSavedSearchesStorage(storage);
  return persistControlCenterSavedSearches(companyId, {
    defaultId: normalized.defaultId === searchId ? null : normalized.defaultId,
    searches: normalized.searches.filter((search) => search.id !== searchId),
  });
};

export const setDefaultSearch = (companyId, storage, searchId) => {
  const normalized = normalizeSavedSearchesStorage(storage);
  const exists = normalized.searches.some((search) => search.id === searchId);
  return persistControlCenterSavedSearches(companyId, {
    ...normalized,
    defaultId: exists ? searchId : normalized.defaultId,
  });
};

export const clearDefaultSearch = (companyId, storage) => {
  const normalized = normalizeSavedSearchesStorage(storage);
  return persistControlCenterSavedSearches(companyId, { ...normalized, defaultId: null });
};

export const renameSearch = (companyId, storage, searchId, name) => {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (!trimmedName) {
    throw new Error('Saved search name is required.');
  }

  const normalized = normalizeSavedSearchesStorage(storage);
  return persistControlCenterSavedSearches(companyId, {
    ...normalized,
    searches: normalized.searches.map((search) => (
      search.id === searchId
        ? { ...search, name: trimmedName, updatedAt: new Date().toISOString() }
        : search
    )),
  });
};

export const findSearchById = (storage, searchId) => {
  const normalized = normalizeSavedSearchesStorage(storage);
  return normalized.searches.find((search) => search.id === searchId) || null;
};

export const getDefaultSearch = (storage) => {
  const normalized = normalizeSavedSearchesStorage(storage);
  if (!normalized.defaultId) return null;
  return normalized.searches.find((search) => search.id === normalized.defaultId) || null;
};
